import { useTranslation } from 'next-i18next'
import Image from '@components/ui/image'
import StarRatingComponent from 'react-star-rating-component'
import { IoStarSharp } from 'react-icons/io5'
import ProgressBar from '@components/common/progress-bar'
import Button from '@components/ui/button'
import { useEffect, useState } from 'react'
import { FC } from 'react'
import { useModalAction } from '@components/common/modal/modal.context'
import { categoryPlaceholder } from '@assets/placeholders'
import { RiArrowDownSLine } from 'react-icons/ri'
import OutlineButton from '@components/ui/outline-button'

function classNames(...classes: any) {
  return classes.filter(Boolean).join(' ')
}
interface CustomerReviewsProps {
  sku: string
  reviews: any
}

const CustomerReviews: FC<CustomerReviewsProps> = ({ sku, reviews }) => {
  const { t } = useTranslation()
  const { openModal } = useModalAction()
  const [limit, setLimit] = useState(5)
  const writeReview = () => {
    openModal('WRITE_REVIEWS', { sku })
  }
  const getReviewDate = (review) => {
    const createdDate = new Date(review.created_at.replace(' ', 'T'))
    return createdDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const getProgress = (progressindex: number) => {
    return reviews.data.stars[progressindex] === 0
      ? 0
      : Math.floor(
          (reviews.data.stars[progressindex] / reviews.data.totalCount) * 100,
        )
  }

  if (
    !reviews?.reviewData ||
    reviews?.reviewData.constructor !== Array ||
    !reviews?.reviewData.length
  )
    return (
      <div className="text-center text-brand-dark my-5 d-flex align-items-center flex-column">
        <StarRatingComponent
          name="ratings"
          starCount={5}
          value={0}
          starColor="#F3B81F"
          editing={false}
          emptyStarColor="#DFE6ED"
          renderStarIcon={() => (
            <IoStarSharp fontSize={20} className="-mr-1 text-gray-300" />
          )}
        />
        <h3 className="my-2 text-lg sm:text-xl font-bold">
          No customer reviews or ratings yet
        </h3>
        <p className="text-sm">Share your thoughts with other customers</p>
        <div className="mt-2">
          <OutlineButton
            className=" hover:bg-brand-light rounded  h-[50px] mt-5  px-3 text-brand-dark  whitespace-nowrap"
            onClick={writeReview}
          >
            Write a Customer Review
          </OutlineButton>
        </div>
      </div>
    )
  return (
    <>
      <div className="shadow-box flex flex-wrap-reverse md:flex-nowrap p-5 bg-brand-light mt-5 shadow-card">
        <div className="w-full md:max-w-[366px] md:min-w-[266px] pt-2 px-3 md:px-0 md:border-r text-center md:pr-5">
          <h2 className="text-brand-gray font-medium text-xl font-poppins">
            {t('text-over-all')}
          </h2>
          <div className="flex items-center text-center  justify-center gap-2 mb-5">
            <StarRatingComponent
              name="ratings"
              starCount={5}
              value={reviews.data.roundOf}
              starColor="#FFA502"
              editing={false}
              emptyStarColor="#DFE6ED"
              renderStarIcon={() => (
                <IoStarSharp fontSize={15} className="-mr-1 " />
              )}
            />
            <span className="text-brand-gray  font-medium text-[12px] font-sofia mt-[2px]">
              {reviews.reviewData.length} {t('text-reviews')}
            </span>
          </div>

          <ProgressBar stars={5} progress={getProgress(4)} />
          <ProgressBar stars={4} progress={getProgress(3)} />
          <ProgressBar stars={3} progress={getProgress(2)} />
          <ProgressBar stars={2} progress={getProgress(1)} />
          <ProgressBar stars={1} progress={getProgress(0)} />

          <Button
            className="mt-2  h-8 md:h-9 lg:text-[14px]  font-sofia font-medium"
            onClick={writeReview}
          >
            {t('text-write-review')}
          </Button>
        </div>
        <div className="pl-3 w-full">
          {reviews.reviewData.slice(0, limit).map((review, index) => (
            <div
              className={` ${
                reviews.length - 1 !== index ? 'border-b-2' : ''
              } py-3`}
              key={index}
            >
              <div className="flex">
                <div className="pr-2">
                  <Image
                    src={categoryPlaceholder}
                    alt="reviewer-icon"
                    className="rounded-full"
                    width={33}
                    height={33}
                  />
                </div>
                <div className="-mt-[3px]">
                  <h2 className="text-brand-dark  font-medium ">
                    {review.nickname}
                  </h2>
                  <p className="italic text-xs text-brand-dark font-sofia">
                    {getReviewDate(review)}
                  </p>
                  <div className="flex items-center text-center gap-1 mb-3 mt-2">
                    <StarRatingComponent
                      name="ratings"
                      starCount={5}
                      value={review.ratings[0]?.value}
                      starColor="#FFA502"
                      emptyStarColor="#DFE6ED"
                      renderStarIcon={() => (
                        <IoStarSharp fontSize={13} className="-mr-1" />
                      )}
                    />
                    <span className="text-brand-gray text-xs font-poppins font-medium">
                      {review.ratings[0]?.value.toFixed(1)}
                    </span>
                  </div>
                </div>
              </div>
              <p
                className="text-sm ml-3 xs:pl-6 font-poppins text-brand-gray"
                style={{ lineBreak: 'anywhere' }}
              >
                {review.detail}
              </p>
            </div>
          ))}
        </div>
      </div>

      {reviews?.reviewData?.length > 5 && (
        <p
          onClick={() =>
            setLimit(limit === 5 ? reviews?.reviewData?.length : 5)
          }
          className="flex cursor-pointer p-3 text-center justify-center"
        >
          {/* see all {reviews?.reviewData?.length} reviews */}
          <span className="text-brand cursor-pointer font-medium underline">
            {limit === 5
              ? `See all ${reviews?.reviewData?.length} reviews`
              : 'Show Less'}
          </span>
          <RiArrowDownSLine className="text-brand" fontSize={28} />
        </p>
      )}
    </>
  )
}

export default CustomerReviews
